import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Switch,
    Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";

import DashboardHeader from "@/components/app/DashboardHeader";
import DashboardContent from "@/components/app/DashboardContent";
import { useGetHomeCategorySelectorSettings, useUpdateHomeCategorySelectorSettings } from "@/api/hooks/useSystemSettings";
import { IUpdateHomeCategorySelectorPayload, THomeCategory } from "@/api/types/system-settings";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { getAxiosError } from "@/utils/get-axios-error";

const CATEGORY_LABELS: Record<THomeCategory, string> = {
    all: "All",
    education: "Education",
    entertainment: "Entertainment",
};

const PAGE_SUBTITLE = "Configure the category tabs the app shows on top of the home page";

const CategorySelector: React.FC = () => {
    const { data, isLoading, isError } = useGetHomeCategorySelectorSettings();
    const { mutate: updateSettings, isPending } = useUpdateHomeCategorySelectorSettings();
    const [enabled, setEnabled] = useState<boolean>(false);
    const [defaultCategory, setDefaultCategory] = useState<THomeCategory | null>(null);

    const settings = data?.data;
    const availableCategories = settings?.availableCategories ?? [];
    // The backend refuses to enable a selector that would show fewer than two tabs.
    const canEnable = availableCategories.length > 1;
    // A category can lose its active home ui after it was saved as the default, so
    // keep showing it — disabled — instead of leaving the select blank.
    const selectableCategories =
        defaultCategory && !availableCategories.includes(defaultCategory)
            ? [...availableCategories, defaultCategory]
            : availableCategories;

    useEffect(() => {
        if (settings) {
            setEnabled(settings.enabled);
            setDefaultCategory(settings.defaultCategory);
        }
    }, [settings]);

    const persist = (changes: Required<IUpdateHomeCategorySelectorPayload>, successMessage: string) => {
        const previous = { enabled, defaultCategory };

        setEnabled(changes.enabled);
        setDefaultCategory(changes.defaultCategory);

        updateSettings(changes, {
            onSuccess: () => {
                enqueueSnackbar(successMessage, { variant: "success" });
            },
            onError: (err) => {
                // Revert on error
                setEnabled(previous.enabled);
                setDefaultCategory(previous.defaultCategory);

                const error = getAxiosError(err);
                const message = error?.meta.message || "Failed to update the category selector settings";

                enqueueSnackbar(message, { variant: "error" });
            },
        });
    };

    const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        // With the selector on, the app serves a tab per category and there is no single
        // default, so it is cleared. Turning it back off needs one again.
        const nextDefaultCategory = newValue ? null : (defaultCategory ?? availableCategories[0] ?? null);

        persist(
            { enabled: newValue, defaultCategory: nextDefaultCategory },
            `Category selector ${newValue ? "enabled" : "disabled"} successfully`
        );
    };

    const handleDefaultCategoryChange = (event: SelectChangeEvent) => {
        const newValue = event.target.value as THomeCategory;

        persist({ enabled, defaultCategory: newValue }, `Default category set to ${CATEGORY_LABELS[newValue]}`);
    };

    return (
        <>
            <DashboardHeader title="Home Category Selector">
                <Typography component="p">{PAGE_SUBTITLE}</Typography>
            </DashboardHeader>
            <DashboardContent>
                {isLoading && (
                    <Box alignItems="center" display="flex" justifyContent="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                )}

                {isError && !isLoading && (
                    <Alert severity="error">Failed to load the category selector settings. Please try again later.</Alert>
                )}

                {!isLoading && !isError && (
                    <Box sx={{ p: 3 }}>
                        {availableCategories.length === 0 && (
                            <Alert
                                action={
                                    <Button
                                        color="inherit"
                                        component={Link}
                                        size="small"
                                        to={ROUTE_CONSTANTS.APP_UI_MANAGEMENT.HOME.ROOT.ABSOLUTE}
                                    >
                                        Home Page
                                    </Button>
                                }
                                severity="warning"
                                sx={{ mb: 3 }}
                            >
                                No category has an active home UI, so the app has nothing to serve. Activate a home UI first.
                            </Alert>
                        )}

                        {availableCategories.length === 1 && (
                            <Alert
                                action={
                                    <Button
                                        color="inherit"
                                        component={Link}
                                        size="small"
                                        to={ROUTE_CONSTANTS.APP_UI_MANAGEMENT.HOME.ROOT.ABSOLUTE}
                                    >
                                        Home Page
                                    </Button>
                                }
                                severity="info"
                                sx={{ mb: 3 }}
                            >
                                Only <strong>{CATEGORY_LABELS[availableCategories[0]]}</strong> has an active home UI. The
                                selector needs at least two categories before it can be turned on.
                            </Alert>
                        )}

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabled}
                                    color="primary"
                                    disabled={isPending || (!enabled && !canEnable)}
                                    onChange={handleToggle}
                                />
                            }
                            label={
                                <Typography component="span" variant="body1">
                                    Show the category selector in the app
                                </Typography>
                            }
                        />

                        {!enabled && (
                            <Box sx={{ mt: 3, maxWidth: 320 }}>
                                <FormControl fullWidth disabled={isPending || availableCategories.length === 0} size="small">
                                    <InputLabel id="default-category-label">Default category</InputLabel>
                                    <Select
                                        id="default-category"
                                        label="Default category"
                                        labelId="default-category-label"
                                        value={defaultCategory ?? ""}
                                        onChange={handleDefaultCategoryChange}
                                    >
                                        {selectableCategories.map((category) => (
                                            <MenuItem
                                                key={category}
                                                disabled={!availableCategories.includes(category)}
                                                value={category}
                                            >
                                                {CATEGORY_LABELS[category]}
                                                {!availableCategories.includes(category) && " (no active home UI)"}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        <Box sx={{ mt: 3 }}>
                            <Typography paragraph color="text.secondary" variant="body2">
                                {enabled
                                    ? "The category selector is currently visible. The app shows a tab per category with an active home UI and lets the user switch between them, so no default category is stored."
                                    : "The category selector is currently hidden. Every user is served the default category and cannot switch."}
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                                <strong>When enabled:</strong> the app shows a tab per category with an active home UI, and
                                the default category is cleared.
                            </Typography>
                            <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                                <strong>When disabled:</strong> no tabs are shown and every user gets the home UI of the
                                default category.
                            </Typography>
                            <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                                A category can only be offered while it has an active home UI, which is managed under App UI
                                Management → Home Page. Changes reach the apps within 5 minutes.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DashboardContent>
        </>
    );
};

export default CategorySelector;
